// CREATE-COVER 2026-07-04: client-side mirror of the backend's auto_cover_for (ssh dev box,
// /app/api/routers/coordination_objects.py inside the coowe-api-dev container -- the checked-out
// coowe-platform-git worktree on that box is stale/does not contain this function, so it was read
// live from the running container, not from a repo grep). DEFAULT_COVERS / _COVER_TOPICS /
// _COVER_PHRASES / _stable_hash below are byte-for-byte the same tables and algorithm as the
// backend so a client-picked "topic aligned" cover for a given name is IDENTICAL to what the
// backend would assign the same TeeUp if it ever had to auto-cover it server-side (it won't, for
// web creates, once the client always sends a real cover -- see rule 3 of the CREATE-COVER task).
// Keep in sync manually: if the backend table changes, re-pull it via
//   ssh ubuntu@132.145.135.180 "docker exec coowe-api-dev sed -n '54,108p' /app/api/routers/coordination_objects.py"
// and update both tables + phrase list here.

export const DEFAULT_COVERS = [
  "https://images.unsplash.com/photo-1511578314322-379afb476865?w=1080&q=80", // 0 party/gathering
  "https://images.unsplash.com/photo-1517457373958-b7bdd4587205?w=1080&q=80", // 1 coworking/meeting
  "https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=1080&q=80", // 2 restaurant/dinner
  "https://images.unsplash.com/photo-1447279506476-3faec8071eee?w=1080&q=80", // 3 coffee
  "https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=1080&q=80", // 4 team/friends
  "https://images.unsplash.com/photo-1530103862676-de8c9debad1d?w=1080&q=80", // 5 celebration
  "https://images.unsplash.com/photo-1496024840928-4c417adf211d?w=1080&q=80", // 6 picnic/outdoors
  "https://images.unsplash.com/photo-1543007630-9710e4a00a20?w=1080&q=80", // 7 concert/event
  "https://images.unsplash.com/photo-1552674605-db6ffd4facb5?w=1080&q=80", // 8 dinner table
  "https://images.unsplash.com/photo-1516450360452-9312f5e86fc7?w=1080&q=80", // 9 brunch
  "https://images.unsplash.com/photo-1523580494863-6f3031224c94?w=1080&q=80", // 10 group toast
  "https://images.unsplash.com/photo-1531058020387-3be344556be6?w=1080&q=80", // 11 gathering table
  "https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=1080&q=80", // 12 meeting/handshake
  "https://images.unsplash.com/photo-1543269865-cbf427effbad?w=1080&q=80", // 13 study/collab
  "https://images.unsplash.com/photo-1533174072545-7a4b6ad7a6c3?w=1080&q=80", // 14 drinks
  "https://images.unsplash.com/photo-1519671482749-fd09be7ccebf?w=1080&q=80", // 15 friends outdoor
]

// keyword -> candidate cover indices. Whole-word token match (a trailing "*" = prefix stem, e.g.
// "celebrat*" matches celebrate/celebration). Order matters -- first match wins, same as backend.
const COVER_TOPICS = [
  [["coffee", "coffees", "espresso", "latte", "cappuccino", "cafe", "café"], [3]],
  [
    [
      "lunch", "dinner", "dinners", "brunch", "breakfast", "food", "restaurant", "meal", "meals",
      "pizza", "sushi", "taco", "tacos", "bbq", "barbecue",
    ],
    [2, 8, 9],
  ],
  [["drink", "drinks", "beer", "beers", "wine", "wines", "cocktail", "cocktails", "pub"], [10, 14]],
  [
    [
      "meeting", "meetings", "standup", "sync", "review", "planning", "interview", "kickoff",
      "retro", "sprint", "demo", "1:1",
    ],
    [1, 12, 13],
  ],
  [
    ["party", "parties", "birthday", "wedding", "anniversary", "graduation", "reunion", "celebrat*"],
    [0, 5, 10],
  ],
  [
    ["hike", "hikes", "hiking", "picnic", "park", "beach", "outdoor", "outdoors", "camping", "camp", "trail"],
    [6, 15],
  ],
  [["concert", "festival", "movie", "gig", "match", "game", "games"], [7]],
]

// multi-word phrase -> indices (substring match on the raw lowercased name), checked BEFORE the
// token table, same as backend.
const COVER_PHRASES = [
  ["happy hour", [10, 14]],
  ["stand up", [1, 12, 13]],
  ["stand-up", [1, 12, 13]],
]

// Deterministic 32-bit hash matching the backend's _stable_hash (and the FE's existing
// defaultCoverFor char*31 rolling hash in @utils/localCoverImage) so the same name resolves to the
// same pick on both ends.
const stableHash = s => {
  const str = String(s ?? "")
  let h = 0
  for (let i = 0; i < str.length; i++) h = (h * 31 + str.charCodeAt(i)) >>> 0
  return h
}

// Mirrors api/routers/coordination_objects.py::auto_cover_for exactly (phrase check, then
// whole-word/prefix-stem token check, then full-set fallback, all deterministic by name hash).
export const autoCoverFor = name => {
  const nm = String(name || "").toLowerCase()
  const tokens = nm.match(/[a-z0-9:]+/g) || []
  let candidates = null

  for (const [phrase, idxs] of COVER_PHRASES) {
    if (nm.includes(phrase)) {
      candidates = idxs
      break
    }
  }

  if (!candidates) {
    topicLoop: for (const [keywords, idxs] of COVER_TOPICS) {
      for (const k of keywords) {
        if (k.endsWith("*")) {
          const stem = k.slice(0, -1)
          if (tokens.some(t => t.startsWith(stem))) {
            candidates = idxs
            break topicLoop
          }
        } else if (tokens.includes(k)) {
          candidates = idxs
          break topicLoop
        }
      }
    }
  }

  if (!candidates) candidates = DEFAULT_COVERS.map((_, i) => i)

  const pick = candidates[stableHash(name) % candidates.length]
  return DEFAULT_COVERS[pick]
}
